import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/modules/common/api/index.ts';
import { TASKS_TIMEZONE } from '@/modules/common/helpers/taskDateTime.ts';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { CURRENT_USER_TASKS_LIST_QUERY_KEY } from '@/modules/common/queries/tasks.ts';
import { GetTaskListV2Response, GetTaskListV2TasksGetParams, TaskListMode } from 'compliance-api';

export const useTasksListQuery = (payload: GetTaskListV2TasksGetParams, disabled = false) => {
  const { page, ...data } = payload;
  const businessId = useGetCurrentBusinessId();

  return useInfiniteQuery<GetTaskListV2Response>({
    queryKey: [CURRENT_USER_TASKS_LIST_QUERY_KEY, JSON.stringify(data), businessId],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    refetchInterval: 1000 * 60 * 5, //5min
    refetchOnMount: 'always',
    queryFn: async ({ pageParam = 0 }) => {
      const resolvedPage =
        typeof pageParam === 'object' && pageParam !== null && 'page' in pageParam
          ? Number(pageParam.page)
          : Number(pageParam || page);
      const params = {
        ...payload,
        page: resolvedPage,
        timezone: TASKS_TIMEZONE,
      };

      switch (payload.mode) {
        case TaskListMode.Todo:
          return (await api.v2.getTodoTaskListV2TasksTodoGet(params)).data as GetTaskListV2Response;
        case TaskListMode.Upcoming:
          return (await api.v2.getUpcomingTaskListV2TasksUpcomingGet(params))
            .data as GetTaskListV2Response;
        case TaskListMode.History:
          return (await api.v2.getHistoryTaskListV2TasksHistoryGet(params))
            .data as GetTaskListV2Response;
        case TaskListMode.Overdue:
          return (await api.v2.getOverdueTaskListV2TasksOverdueGet(params))
            .data as GetTaskListV2Response;
        case TaskListMode.AllTasks:
          return (await api.v2.getAllTaskListV2TasksAllGet(params)).data as GetTaskListV2Response;
        case TaskListMode.Deleted:
          return (await api.v2.getDeletedTaskListV2TasksDeletedGet(params))
            .data as GetTaskListV2Response;
        default:
          return (await api.v2.getTaskListV2TasksGet(params)).data;
      }
    },
    retry: 2,
    getNextPageParam: ({ total, page, page_size }) => {
      const totalPage = Math.ceil(total / page_size);
      const hasMore = totalPage > page;
      if (hasMore) {
        return page + 1;
      }
    },
    enabled: !disabled,
  });
};
